import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { StampSerial, FlagPill } from "@/components/shared";
import { UploadCloud, Loader2, CheckCircle2, AlertTriangle, ArrowRight, FileSpreadsheet, Trash2, History } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, tone }) {
  const tones = { good: "text-positive", warn: "text-brass", bad: "text-brick", ink: "text-ink" };
  return (
    <div className="bg-white border border-hairline p-4 rounded-sm">
      <div className="text-xs uppercase tracking-wider text-slate">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${tones[tone] || "text-ink"}`}>{value}</div>
    </div>
  );
}

export default function ImportWizard() {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [balances, setBalances] = useState({});
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [delId, setDelId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadBatches = () => api.get("/import/batches").then((r) => setBatches(r.data.batches)).catch(() => {});
  useEffect(() => { loadBatches(); }, [result]);

  const deleteBatch = async () => {
    setDeleting(true);
    try {
      const r = await api.delete(`/import/batch/${delId}`);
      toast.success(`Import dipadam — ${r.data.members_removed} ahli dibuang`);
      setDelId(null);
      loadBatches();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setDeleting(false); }
  };

  const runDryRun = async () => {
    if (!file) return toast.error("Please choose an .xlsx file");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/import/dry-run", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data);
      const init = {};
      r.data.rows.forEach((row) => {
        if (row.importable && !row.already_exists && row.share_value != null) init[row.membership_no] = String(row.share_value);
      });
      setBalances(init);
      setStep(2);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const commit = async () => {
    setLoading(true);
    try {
      const confirmed = {};
      Object.entries(balances).forEach(([k, v]) => { if (v !== "" && v != null) confirmed[k] = parseFloat(v); });
      const r = await api.post("/import/commit", { batch_id: report.batch_id, confirmed_balances: confirmed });
      setResult(r.data);
      setStep(3);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const steps = ["Muat Naik / Upload", "Semak & Sahkan / Review", "Selesai / Done"];

  return (
    <Layout title="Import Excel Register" subtitle="Dry-run → confirm opening balances → commit (idempotent)">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm ${step === i + 1 ? "bg-ink text-white" : step > i + 1 ? "bg-positive-bg text-positive" : "bg-white border border-hairline text-slate"}`}>
              <span className="font-mono text-xs">{step > i + 1 ? "✓" : i + 1}</span> {s}
            </div>
            {i < 2 && <ArrowRight size={14} className="text-slate/40" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="max-w-xl">
          <label className="block border-2 border-dashed border-hairline rounded-md bg-white p-10 text-center cursor-pointer hover:border-brass transition-colors" data-testid="upload-dropzone">
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="file-input" />
            <UploadCloud size={40} className="text-brass mx-auto mb-3" strokeWidth={1.5} />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-ink"><FileSpreadsheet size={18} className="text-positive" /> {file.name}</div>
            ) : (
              <>
                <div className="text-ink font-medium">Pilih fail Excel / Choose Excel file</div>
                <div className="text-xs text-slate mt-1">Sheet with "REGISTER OF MEMBERS", data from row 6</div>
              </>
            )}
          </label>
          <div className="mt-4 bg-brass/5 border border-brass/20 rounded-sm p-4 text-sm text-slate">
            <strong className="text-ink">Dry-run dahulu.</strong> Tiada data ditulis sehingga anda semak. No & baki syer (lajur Amount/Share) diambil terus dari Excel dan disambung dari No terakhir. Padanan pada No. Ahli / IC — tiada pendua.
          </div>
          <button onClick={runDryRun} disabled={loading || !file} data-testid="dryrun-btn" className="mt-6 bg-ink text-white hover:bg-ink-light px-5 py-2.5 rounded-sm text-sm flex items-center gap-2 disabled:opacity-50 transition-colors">
            {loading && <Loader2 size={16} className="animate-spin" />} Jalankan Dry-Run / Run Dry-Run
          </button>

          {batches.length > 0 && (
            <div className="mt-10 bg-white border border-hairline rounded-sm">
              <div className="px-5 py-3 border-b border-hairline flex items-center gap-2"><History size={16} className="text-brass" /><h3 className="font-serif text-lg text-ink">Sejarah Import / Import History</h3></div>
              <div className="divide-y divide-hairline">
                {batches.map((b) => (
                  <div key={b.id} className="flex items-center justify-between px-5 py-3" data-testid={`batch-${b.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-ink truncate flex items-center gap-2"><FileSpreadsheet size={14} className="text-slate" /> {b.filename}</div>
                      <div className="text-xs text-slate mt-0.5">{(b.created_at || "").slice(0, 16).replace("T", " ")} · {b.live_member_count} ahli dalam sistem{b.status !== "committed" ? " · (dry-run sahaja)" : ""}</div>
                    </div>
                    {b.live_member_count > 0 && (
                      <button onClick={() => setDelId(b.id)} data-testid={`delete-batch-${b.id}`} className="flex items-center gap-1.5 bg-transparent border border-brick/30 text-brick hover:bg-brick-bg px-3 py-1.5 rounded-sm text-xs transition-colors flex-shrink-0"><Trash2 size={14} /> Padam Import</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {step === 2 && report && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Total Rows" value={report.summary.total_rows} tone="ink" />
            <Stat label="Baru / New" value={report.new_count} tone="good" />
            <Stat label="Duplikat / Existing" value={report.duplicate_count} tone="warn" />
            <Stat label="Ditanda / Flagged" value={report.summary.flagged_rows} tone={report.summary.flagged_rows ? "bad" : "good"} />
          </div>

          <div className="bg-brass/5 border border-brass/20 rounded-sm p-4 text-sm text-slate mb-6 flex gap-3">
            <AlertTriangle size={18} className="text-brass flex-shrink-0 mt-0.5" />
            <div>Opening balances are <strong className="text-ink">never auto-imported</strong>. Confirm or re-key each Share (RM) below. Blank confirmations create the member without an opening-balance transaction (excluded from reconciliation until confirmed). Sheet: <span className="font-mono text-ink">{report.summary.sheet_used}</span>.</div>
          </div>

          <div className="bg-white border border-hairline rounded-sm overflow-hidden">
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0">
                  <tr className="bg-paper border-y border-ink/20">
                    <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Row</th>
                    <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Membership</th>
                    <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Name</th>
                    <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Share (raw)</th>
                    <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Confirm RM</th>
                    <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Flags / State</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.row_index} className={`ledger-row ${!r.importable ? "bg-brick-bg/40" : r.already_exists ? "opacity-50" : ""}`} data-testid={`import-row-${r.row_index}`}>
                      <td className="py-2 px-4 font-mono text-xs text-slate">{r.row_index}</td>
                      <td className="py-2 px-4"><StampSerial value={r.membership_no} /></td>
                      <td className="py-2 px-4 text-sm text-ink">{r.name || <span className="text-slate/40">—</span>}</td>
                      <td className="py-2 px-4 font-mono text-xs text-slate">{r.share_raw ?? "—"}</td>
                      <td className="py-2 px-4">
                        {r.importable && !r.already_exists ? (
                          <input type="number" step="0.01" value={balances[r.membership_no] ?? ""} onChange={(e) => setBalances({ ...balances, [r.membership_no]: e.target.value })} placeholder="—" data-testid={`balance-${r.membership_no}`} className="w-28 bg-white border border-slate-300 rounded-sm px-2 py-1 text-sm font-mono focus:border-brass focus:ring-1 focus:ring-brass outline-none text-ink" />
                        ) : <span className="text-xs text-slate/40">—</span>}
                      </td>
                      <td className="py-2 px-4">
                        {!r.importable && <FlagPill flag="missing_membership_no" />}
                        {r.already_exists && <span className="text-xs text-brass italic">exists — skip</span>}
                        {r.importable && !r.already_exists && (r.flags || []).map((f) => <FlagPill key={f} flag={f} />)}
                        {r.importable && !r.already_exists && (r.flags || []).length === 0 && <span className="text-xs text-positive">ready</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(1)} className="bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-4 py-2 rounded-sm text-sm">← Semula / Back</button>
            <button onClick={commit} disabled={loading} data-testid="commit-btn" className="bg-brass text-ink font-semibold hover:bg-brass-hover px-5 py-2.5 rounded-sm text-sm flex items-center gap-2 disabled:opacity-60 transition-colors">
              {loading && <Loader2 size={16} className="animate-spin" />} Commit Import ({report.new_count} baru)
            </button>
          </div>
        </div>
      )}

      {step === 3 && result && (
        <div className="max-w-lg bg-white border border-hairline rounded-sm p-8 text-center" data-testid="import-result">
          <CheckCircle2 size={48} className="text-positive mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-ink">Import Selesai / Complete</h2>
          <div className="grid grid-cols-3 gap-4 mt-6">
            <Stat label="Created" value={result.created} tone="good" />
            <Stat label="Skipped" value={result.skipped} tone="warn" />
            <Stat label="Balances" value={result.balances_created} tone="ink" />
          </div>
          <button onClick={() => { setStep(1); setFile(null); setReport(null); setResult(null); }} className="mt-8 bg-ink text-white hover:bg-ink-light px-5 py-2.5 rounded-sm text-sm transition-colors">Import Lagi / Import Another</button>
        </div>
      )}

      {delId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={() => !deleting && setDelId(null)}>
          <div className="bg-white rounded-md shadow-2xl w-full max-w-sm p-6 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-ink flex items-center gap-2"><AlertTriangle size={20} className="text-brick" /> Padam Import?</h3>
            <p className="text-sm text-slate mt-2">Semua ahli yang dimasukkan melalui import ini akan dibuang dari sistem beserta baki syer mereka. Tindakan ini tidak boleh diundur.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setDelId(null)} disabled={deleting} className="bg-transparent border border-ink/20 text-ink px-4 py-2 rounded-sm text-sm">Batal</button>
              <button onClick={deleteBatch} disabled={deleting} data-testid="confirm-delete-batch" className="bg-brick text-white hover:bg-brick/90 px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-60">{deleting && <Loader2 size={14} className="animate-spin" />} Ya, Padam</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
